import { useEffect, useState } from 'react';
import { LeadStatusHistory } from './LeadStatusHistory';
import { LeadStatusChangeModal } from './LeadStatusChangeModal';
import type { NegativeCloseExtras, PositiveCloseExtras } from './LeadStatusChangeModal';
import { useLeadRepository, useLeadEventRepository } from '../../../../rpc';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Lead, UpdateLead } from '../../../../../schemas/lead.schema';
import type { LeadEvent } from '../../../../../schemas/lead-event.schema';
import type { LeadStatus } from '../../../../../schemas/lead-status.schema';

/**
 * Минимальный набор полей лида, необходимый для отрисовки funnel-а и смены статуса.
 *
 * Структурный тип — подходит и для LeadExtended из LeadView, и для PartnerLeadExtended
 * из карточки партнёра. Главное чтобы был id, текущий статус и тип.
 */
export type LeadFunnelLead = Pick<Lead, 'id'> & {
  lead_status_id?: number | null;
  lead_type_id?: number | null;
  lead_status?: LeadStatus | null;
};

interface LeadFunnelProps {
  lead: LeadFunnelLead;
  /**
   * Колбек после успешной смены статуса.
   * Обычно — перезагрузка карточки родителя (LeadView.reload или PartnerView.reload).
   */
  onChanged: () => void | Promise<void>;
}

/**
 * Интерактивный funnel лида: визуализация статусов + клик-смена через модалку подтверждения.
 *
 * Самодостаточный компонент — сам подгружает свою историю смены статусов
 * (`leadEventRepository.getStatusHistoryForLead`), сам управляет модалкой,
 * сам делает `leadRepository.update`. Родитель только передаёт лида и `onChanged`.
 *
 * Используется одинаково в LeadView и в карточке партнёра.
 */
export function LeadFunnel({ lead, onChanged }: LeadFunnelProps) {
  const leadRepository = useLeadRepository();
  const leadEventRepository = useLeadEventRepository();

  const [statusHistory, setStatusHistory] = useState<LeadEvent[]>([]);
  const [targetStatus, setTargetStatus] = useState<LeadStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);

  // Подгружаем историю при смене лида или его текущего статуса
  // (после успешного апдейта родитель пришлёт лид с новым lead_status_id → перезагрузим).
  useEffect(() => {
    let cancelled = false;
    leadEventRepository
      .getStatusHistoryForLead(lead.id)
      .then(events => {
        if (!cancelled) setStatusHistory(events ?? []);
      })
      .catch(err => {
        logger.error('Failed to load lead status history:', err);
        if (!cancelled) setStatusHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [lead.id, lead.lead_status_id, leadEventRepository]);

  const opened = targetStatus !== null;

  const close = () => {
    if (changing) return;
    setTargetStatus(null);
    setError(null);
  };

  const confirm = async (extras?: NegativeCloseExtras | PositiveCloseExtras) => {
    if (!targetStatus) return;
    setChanging(true);
    setError(null);
    try {
      const patch: UpdateLead = { lead_status_id: targetStatus.id };
      if (extras && 'lost_reason_id' in extras) {
        patch.lost_reason_id = extras.lost_reason_id;
        patch.lost_comment = extras.lost_comment;
      } else if (extras && 'won_comment' in extras) {
        patch.won_comment = extras.won_comment;
      }
      await leadRepository.update(lead.id, patch);
      setTargetStatus(null);
      await onChanged();
    } catch (err) {
      logger.error('Failed to change lead status:', err);
      setError(err instanceof Error ? err.message : 'Не удалось изменить статус лида');
    } finally {
      setChanging(false);
    }
  };

  return (
    <>
      <LeadStatusHistory
        statusHistory={statusHistory}
        currentStatusId={lead.lead_status_id ?? null}
        leadTypeId={lead.lead_type_id ?? null}
        onStatusClick={setTargetStatus}
      />

      <LeadStatusChangeModal
        opened={opened}
        currentStatus={lead.lead_status ?? null}
        targetStatus={targetStatus}
        error={error}
        changing={changing}
        onConfirm={confirm}
        onClose={close}
      />
    </>
  );
}
